import type {Dispatch} from '@reduxjs/toolkit'
import type {AuthContextProps} from '@containers/App/AuthProvider'
import {handleUnauthorized, setAuthHeaders} from '@apis/helpers'
import {isDashboardPrintPath} from '@shared/utils/printRoute'
import {getToken} from '@app/util-functions'

/**
 * Single-use WebSocket tickets (AB#6423, AB#6314).
 *
 * pm-core, pm-edge and ai-core no longer authenticate a socket from the
 * session alone: the client POSTs `/{service}/api/ws-ticket/` with the normal
 * auth headers and connects with `?ticket=<ticket>`. A ticket is single use and
 * lives 30 s, so EVERY connect and EVERY reconnect attempt must call
 * `openServiceSocket` again — never cache the URL or reuse a ticket.
 */

export type WebSocketService = 'core' | 'edge' | 'aicore'

/** Server closed: unauthenticated or session expired. Re-ticket and reconnect with backoff. */
export const WS_CLOSE_UNAUTHENTICATED = 4401
/** Server closed: forbidden. Do not reconnect. */
export const WS_CLOSE_FORBIDDEN = 4403

type TicketResponse = {ticket?: unknown; expires_in?: unknown}

/**
 * Thrown when the ticket POST answered 401: the 401 handling (logout, or the
 * Teams re-auth event) has already run, so callers must NOT schedule a reconnect.
 */
export class ServiceSocketAuthError extends Error {
  constructor(service: WebSocketService) {
    super(`WebSocket ticket for ${service} refused: unauthenticated`)
    this.name = 'ServiceSocketAuthError'
  }
}

/** Any other ticket failure; callers retry with their existing backoff. */
export class ServiceSocketTicketError extends Error {
  readonly status: number | null

  constructor(service: WebSocketService, status: number | null, message?: string) {
    super(message ?? `WebSocket ticket for ${service} failed with status ${status ?? 'unknown'}`)
    this.name = 'ServiceSocketTicketError'
    this.status = status
  }
}

export const isServiceSocketAuthError = (error: unknown): error is ServiceSocketAuthError =>
  error instanceof ServiceSocketAuthError

type OpenServiceSocketOptions = {
  // Passed through to the shared 401 handling (re-authorize, Teams re-auth, logout).
  authContext?: AuthContextProps | null
  dispatch?: Dispatch
}

const ticketUrl = (service: WebSocketService): string =>
  `${import.meta.env.VITE_APP_API_BASE_URL || ''}/${service}/api/ws-ticket/`

const socketUrl = (path: string, ticket: string): string => {
  const url = `${import.meta.env.VITE_WS_BASE_URL || ''}${path}`
  return `${url}${url.includes('?') ? '&' : '?'}ticket=${encodeURIComponent(ticket)}`
}

const postTicket = (service: WebSocketService): Promise<Response> =>
  fetch(ticketUrl(service), {
    method: 'POST',
    headers: setAuthHeaders(new Headers()),
  })

/**
 * Resolves the single-use ticket to connect with. There is no ticket-less path:
 * every service enforces tickets, so a 404 is an ordinary ticket error.
 */
const fetchTicket = async (
  service: WebSocketService,
  options: OpenServiceSocketOptions,
): Promise<string> => {
  // The dashboard print route (AB#6645) is read-only: no ticket POST, no socket.
  if (isDashboardPrintPath()) {
    throw new ServiceSocketAuthError(service)
  }
  // The token the ticket POST goes out with (setAuthHeaders reads the same),
  // for the "another request already rotated it" check on a 401.
  const requestToken = getToken()
  let response = await postTicket(service)

  if (response.status === 401) {
    const shouldRetry = await handleUnauthorized({...options, requestToken})
    if (!shouldRetry) {
      throw new ServiceSocketAuthError(service)
    }
    response = await postTicket(service)
    if (response.status === 401) {
      throw new ServiceSocketAuthError(service)
    }
  }

  if (!response.ok) {
    throw new ServiceSocketTicketError(service, response.status)
  }

  const body = (await response.json()) as TicketResponse
  if (typeof body?.ticket !== 'string' || !body.ticket) {
    throw new ServiceSocketTicketError(service, response.status, 'WebSocket ticket missing')
  }
  return body.ticket
}

/**
 * Fetches a fresh single-use ticket from `service` and opens `path` with it.
 * `path` is the full socket path, e.g. `/core/ws/incidents/updates/`.
 *
 * Rejects with `ServiceSocketAuthError` after a 401 (logout already handled;
 * do not reconnect) and with any other error for a failed ticket fetch (retry
 * with backoff). The returned socket is not open yet; wire `onopen` as before.
 */
export const openServiceSocket = async (
  service: WebSocketService,
  path: string,
  options: OpenServiceSocketOptions = {},
): Promise<WebSocket> => {
  const ticket = await fetchTicket(service, options)
  return new WebSocket(socketUrl(path, ticket))
}
